import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { Github } from "lucide-react";
import caldeiraLogo from "@/assets/caldeira-logo.png";
import {
  MessageSquare,
  BookOpen,
  Bot,
  Database,
  Shield,
  Sparkles,
} from "lucide-react";

const features = [
  {
    icon: MessageSquare,
    title: "Chat Multi-Modelo",
    description:
      "Converse com os melhores modelos de IA do mercado — GPT, Claude, Gemini, DeepSeek e outros — em uma única interface.",
  },
  {
    icon: BookOpen,
    title: "Biblioteca de Prompts",
    description:
      "Prompts testados e validados por área, prontos para uso. Crie, organize e compartilhe com seu time.",
  },
  {
    icon: Bot,
    title: "Agentes Personalizados",
    description:
      "Configure assistentes especializados para tarefas recorrentes — copywriting, análise de dados, atendimento e mais.",
  },
  {
    icon: Database,
    title: "Base de Conhecimento",
    description:
      "Faça upload de documentos e alimente a IA com o contexto do seu time para respostas mais precisas e relevantes.",
  },
  {
    icon: Shield,
    title: "Segurança e Controle",
    description:
      "Login corporativo com Google SSO, controle de acesso por projeto e conformidade com a LGPD.",
  },
  {
    icon: Sparkles,
    title: "Ambientes por Projeto",
    description:
      "Organize conversas, prompts e agentes por projeto. Cada equipe tem seu espaço com contexto compartilhado.",
  },
];

const LoginPage = () => {
  const { user, loading, signInWithGoogle, signInWithGitHub } = useAuth();
  const [searchParams] = useSearchParams();
  const showGitHub = searchParams.get("dev") === "1";
  const inIframe =
    import.meta.env.VITE_ENV === "lovable_or_local" &&
    window.self !== window.top;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="animate-pulse text-secondary text-sm">Carregando...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-secondary">
      {/* Hero */}
      <section className="flex flex-col items-center justify-center gap-6 px-6 pt-20 pb-16 text-center">
        <img src={caldeiraLogo} alt="Caldeira AI" className="h-10 w-auto" />

        <div className="space-y-3 max-w-lg">
          <h1 className="text-2xl sm:text-3xl font-semibold text-primary">
            Plataforma de IA do Caldeira
          </h1>
          <p className="text-sm sm:text-base text-tertiary leading-relaxed">
            Plataforma interna de inteligência artificial do Instituto Caldeira.
            Centralize chat com IA, biblioteca de prompts, agentes
            personalizados e base de conhecimento — tudo em um ambiente seguro e
            integrado para o seu time.
          </p>
        </div>

        <button
          onClick={signInWithGoogle}
          className="flex items-center justify-center gap-3 h-11 px-8 rounded-lg border border-color bg-surface text-sm font-medium text-primary hover:bg-hover transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Entrar com Google
        </button>

        {showGitHub && (
          <button
            onClick={signInWithGitHub}
            className="flex items-center justify-center gap-3 h-11 px-8 rounded-lg border border-color bg-surface text-sm font-medium text-primary hover:bg-hover transition-colors"
          >
            <Github className="w-5 h-5" />
            Entrar com GitHub
          </button>
        )}

        {inIframe && (
          <p className="text-xs text-tertiary text-center">
            O login abrirá em uma nova aba. Retorne aqui após autenticar.
          </p>
        )}
      </section>

      {/* Features */}
      <section className="px-6 pb-16 max-w-4xl mx-auto">
        <h2 className="text-lg font-semibold text-primary text-center mb-8">
          Funcionalidades
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-xl border border-color bg-surface p-5 space-y-2"
            >
              <f.icon className="w-5 h-5 text-brand-primary" />
              <h3 className="text-sm font-semibold text-primary">{f.title}</h3>
              <p className="text-xs text-tertiary leading-relaxed">
                {f.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* About */}
      <section className="px-6 pb-16 max-w-2xl mx-auto text-center space-y-3">
        <h2 className="text-lg font-semibold text-primary">
          Sobre a plataforma
        </h2>
        <p className="text-sm text-tertiary leading-relaxed">
          O Plataforma de IA do Caldeira foi desenvolvido para o Instituto Caldeira com o
          objetivo de democratizar o acesso à inteligência artificial dentro da
          organização. A plataforma permite que cada área trabalhe com IA de
          forma organizada, segura e colaborativa — sem necessidade de múltiplas
          assinaturas ou ferramentas externas.
        </p>
      </section>

      {/* Footer */}
      <footer className="border-t border-color px-6 py-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-xs text-tertiary">
        <a
          href="/privacidade"
          className="hover:text-secondary underline transition-colors"
        >
          Política de Privacidade
        </a>
        <span className="hidden sm:inline">·</span>
        <span>Desenvolvido por DobraLabs</span>
      </footer>
    </div>
  );
};

export default LoginPage;
